/**
 * OrderBookControls — the explorer's one control row: where you are (breadcrumbs), what
 * you are cutting the book by (axis), and — on the order book only — which cost you are
 * reading (basis). No scope switches live here — the top bar owns site, cadence and window
 * (works-list §3.3); this row only navigates inside what scope has already chosen.
 *
 * ⭐ THE BASIS GROUP IS OPTIONAL (24 Aug 2026). The ORDER BOOK carries two costs on the same
 * row — a part standard and a job actual — so the reader picks. THE MARGIN FACT CARRIES ONE:
 * its basis is a property of the row and does not vary inside a site, so the control there
 * did nothing at Kirkstall, nothing at Oracle, and hid one entity at Group. That surface
 * passes no `basis`, and the group is not drawn.
 */

import React from 'react'
import { ChevronRight, Home, X } from 'lucide-react'
import { periodHeading } from './kit/cellFormat'
import { AXES, AXIS_LABEL, type Axis } from '../hooks/useOrderBookNav'
import type { Basis } from '../viewmodels/orderBook'
import type { PathSegment } from '../types/metrics'

interface Props {
  path: PathSegment[]
  rootLabel: string // what "the whole book" is called at this scope
  axis: Axis
  /** What each axis is CALLED on this surface. The order book's month is the customer's due
   * date; the margin fact's is the invoice date, and calling both "Due month" is exactly the
   * conflation the invoice fact exists to end. Defaults to the order book's own labels. */
  axisLabel?: Record<Axis, string>
  /** Omit on a fact with ONE cost — the basis group is then not drawn at all. */
  basis?: Basis
  /** The grain currently on screen. At Group scope that is `entity` until you descend —
   * the axis is real but sits beneath it, so the control says so rather than looking inert. */
  level: string
  /** A month held off the timeline, narrowing everything below. Shown as a removable chip
   * rather than a crumb: it filters the book, it is not a level in the descent. */
  month: string | null
  onJump: (depth: number) => void
  onAxis: (axis: Axis) => void
  onBasis?: (basis: Basis) => void
  onClearMonth: () => void
}

const CRUMB = 'rounded px-1.5 py-0.5 text-brand hover:bg-info-bg font-medium'
const HERE = 'text-ink font-semibold'
const SEG = 'px-2.5 py-1 text-[11px] font-medium rounded-md'

function Breadcrumbs({ path, rootLabel, onJump }: Pick<Props, 'path' | 'rootLabel' | 'onJump'>): React.JSX.Element {
  return (
    <nav aria-label="Order-book path" className="flex flex-wrap items-center gap-0.5 text-[12.5px]">
      <button type="button" onClick={() => onJump(0)} className={`${CRUMB} inline-flex items-center gap-1 ${path.length === 0 ? HERE : ''}`}>
        <Home size={13} /> {rootLabel}
      </button>
      {path.map((seg, i) => (
        <span key={`${seg.level}:${seg.id}`} className="flex items-center gap-0.5">
          <ChevronRight size={13} className="text-mut2" />
          <button type="button" onClick={() => onJump(i + 1)} className={`${CRUMB} ${i + 1 === path.length ? HERE : ''}`}>{seg.id}</button>
        </span>
      ))}
    </nav>
  )
}

function Group<T extends string>({ label, options, value, onChange }: {
  label: string
  options: readonly (readonly [T, string])[]
  value: T
  onChange: (next: T) => void
}): React.JSX.Element {
  return (
    <div className="inline-flex items-center gap-0.5 rounded-lg bg-panel p-0.5" role="group" aria-label={label}>
      {options.map(([option, text]) => (
        <button
          key={option}
          type="button"
          onClick={() => onChange(option)}
          aria-pressed={value === option}
          className={`${SEG} ${value === option ? 'bg-panel2 text-ink shadow-sm' : 'text-mut hover:text-ink'}`}
        >
          {text}
        </button>
      ))}
    </div>
  )
}

const BASIS_OPTIONS = [['actual', 'Actual'], ['std', 'Standard']] as const

/** The held month, as a removable chip — a filter on the book, not a level in the descent. */
function MonthChip({ month, onClear }: { month: string; onClear: () => void }): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClear}
      title="Release the month and show the whole window"
      className="inline-flex items-center gap-1 rounded-md bg-info-bg px-2 py-0.5 font-mono text-[10.5px] text-brand hover:text-ink"
    >
      {periodHeading(month)}
      <X size={11} />
    </button>
  )
}

export default function OrderBookControls({
  path, rootLabel, axis, axisLabel = AXIS_LABEL, basis, level, month,
  onJump, onAxis, onBasis, onClearMonth,
}: Props): React.JSX.Element {
  const axisOptions = AXES.map((a) => [a, axisLabel[a]] as const)
  const pending = level === 'entity' // the axis applies one level down, inside an entity
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-line bg-panel2 px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <Breadcrumbs path={path} rootLabel={rootLabel} onJump={onJump} />
        {month && <MonthChip month={month} onClear={onClearMonth} />}
      </div>
      <div className="flex flex-wrap items-center gap-2.5">
        <span className="font-mono text-[10px] uppercase tracking-[.08em] text-mut2">
          {pending ? 'then by' : 'by'}
        </span>
        <Group label="Breakdown" options={axisOptions} value={axis} onChange={onAxis} />
        {basis !== undefined && onBasis !== undefined && (
          <Group label="Cost basis" options={BASIS_OPTIONS} value={basis} onChange={onBasis} />
        )}
      </div>
      {pending && (
        <p className="w-full font-mono text-[10px] text-mut2">
          Group shows the two entities first — open one and it breaks down by {axisLabel[axis].toLowerCase()}.
        </p>
      )}
    </div>
  )
}
